/**
 * Shared requisition display-config constants.
 *
 * Relocated here from `src/pages/requisitions/constants/{constants,enums}.ts`
 * so cross-feature consumers (reports, ATS filters) stop importing into the
 * requisitions page layer. The original modules re-export these for
 * within-feature callers.
 *
 * `RequisitionType` is a type-only import (erased at runtime), so the
 * back-reference to the requisitions enums creates no runtime cycle.
 */
import type {
  RequisitionType,
  EmploymentType,
} from '@/constants/requisitions/enums';

/** Requisition status → badge display config (id/label/color). */
export const requisitionStatusConfig = {
  draft: {
    id: 'draft',
    color: '#FAA238',
    label: 'Draft',
  },
  pending_approval: {
    id: 'pending_approval',
    label: 'Pending approval',
    color: '#CC7914',
  },
  approved: {
    id: 'approved',
    label: 'Approved',
    color: '#0B7B69',
  },
  rejected: {
    id: 'rejected',
    label: 'Not approved',
    color: '#FF4141',
  },
  created: {
    id: 'created',
    label: 'Ready to open',
    color: '#0B7B69',
  },
  open: {
    id: 'open',
    label: 'Open',
    color: '#73D017',
  },
  closed: {
    id: 'closed',
    label: 'Closed',
    color: '#C03744',
  },
  archived: {
    id: 'archived',
    label: 'Archived',
    color: '#585858',
  },
  filled: {
    id: 'filled',
    label: 'Filled',
    color: '#585858',
  },
};

/** Employment type → display config (label/color). */
export const EMPLOYMENT_TYPE_CONFIG: Record<
  EmploymentType,
  { label: string; color: string }
> = {
  full_time: { label: 'Full-time', color: 'blue' },
  part_time: { label: 'Part-time', color: 'green' },
  contract: { label: 'Contract', color: 'orange' },
  internship: { label: 'Internship', color: 'cyan' },
  consultant: { label: 'Consultant', color: 'purple' },
  apprentice: { label: 'Apprentice', color: 'gold' },
  temporary: { label: 'Temporary', color: 'gray' },
  seasonal: { label: 'Seasonal', color: 'teal' },
};

/** Requisition type → display config (label/color). */
export const REQUISITION_TYPE_CONFIG: Record<
  RequisitionType,
  { label: string; color: string }
> = {
  new_hire: { label: 'New hire', color: 'blue' },
  backfill: { label: 'Backfill', color: 'purple' },
  internal: { label: 'Internal', color: 'green' },
};

export interface CloseReasonOption {
  label: string;
  value: string;
}

/** Reasons a requisition can be closed (used in the close form + reports). */
export const CLOSE_REASONS: CloseReasonOption[] = [
  { label: 'External hire', value: 'external_hire' },
  { label: 'Internal transfer', value: 'internal_transfer' },
  { label: 'Internal promotion', value: 'internal_promotion' },
  { label: 'Budget cuts', value: 'budget_cuts' },
  { label: 'Hiring freeze', value: 'hiring_freeze' },
  { label: 'On hold', value: 'on_hold' },
  { label: 'Role no longer needed', value: 'role_no_longer_needed' },
  { label: 'Created by mistake', value: 'created_by_mistake' },
  { label: 'Others', value: 'others' },
];

export interface CloseRequisitionPayload {
  rejection_reason: string;
  rejection_reason_description?: string; // Required only when rejection_reason is 'others'
}
